package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.event.ChangeEvent
import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.reactive.Effect
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.jvm.JvmStatic

/**
 * 开关控件。
 *
 * 语义与 [Checkbox] 类似：点击翻转 enabled 并触发 onChange；视觉为开关样式（轨道 + 滑块），由
 * SwitchRenderer 负责绘制。
 */
class Switch private constructor() : UIElement<Switch>() {

    private var enabledFlag: Boolean = false
    private var onChangeHandler: UiEventHandler<ChangeEvent<Boolean>>? = null
    private var boundEnabledSignal: Signal<in Boolean>? = null

    // === 视觉覆盖字段（可空，null = 回退主题 token） ===
    private var trackColorOnValue: Int? = null
    private var trackColorOffValue: Int? = null
    private var thumbColorOnValue: Int? = null
    private var thumbColorOffValue: Int? = null
    private var thumbInsetValue: Float? = null
    private var trackCornerRadiusValue: Float? = null
    private var thumbCornerRadiusValue: Float? = null
    private var hoverOverlayColorValue: Int? = null
    private var hoverOverlayOpacityValue: Float? = null
    private var pressedOverlayColorValue: Int? = null
    private var pressedOverlayOpacityValue: Float? = null
    private var focusOverlayColorValue: Int? = null
    private var focusOverlayOpacityValue: Float? = null

    init {
        focusableField = true
        // 默认 onClick：翻转 enabled
        super.onClick(UiEventHandler { toggle() })
    }

    override fun onClick(handler: UiEventHandler<MouseEvent>?): Switch {
        // 包装：先翻转 enabled，再调用外部 handler
        super.onClick(
            UiEventHandler { e ->
                toggle()
                handler?.accept(e)
            },
        )
        return this
    }

    /** 翻转 enabled 状态并触发 onChange。 */
    fun toggle(): Switch = enabled(!enabledFlag)

    protected override fun self(): Switch = this

    fun enabled(v: Boolean): Switch {
        val old = enabledFlag
        enabledFlag = v
        if (old != v) {
            boundEnabledSignal?.set(v)
            onChangeHandler?.accept(ChangeEvent(old, v))
        }
        return this
    }

    override fun enabled(): Boolean = enabledFlag

    fun onChange(handler: UiEventHandler<ChangeEvent<Boolean>>?): Switch {
        onChangeHandler = handler
        return this
    }

    /** 双向绑定到响应式 Signal。 */
    fun bindEnabled(signal: Signal<in Boolean>): Switch {
        // 逆变投影 Signal<in Boolean>：接收任意可写入 Boolean 的信号（Signal<Boolean>/Signal<Object>…），
        // 写入位放开可直接 set(Boolean)，读取位投影为 Any? 交由 toBooleanValue 解码——无需强转。
        boundEnabledSignal = signal
        lifecycleScopeField.register(Effect { enabledFlag = toBooleanValue(signal.get()) })
        return this
    }

    // === 视觉覆盖 fluent setter / getter ===

    fun trackColorOn(v: Int?): Switch {
        trackColorOnValue = v
        return this
    }

    fun trackColorOn(): Int? = trackColorOnValue

    fun trackColorOff(v: Int?): Switch {
        trackColorOffValue = v
        return this
    }

    fun trackColorOff(): Int? = trackColorOffValue

    fun thumbColorOn(v: Int?): Switch {
        thumbColorOnValue = v
        return this
    }

    fun thumbColorOn(): Int? = thumbColorOnValue

    fun thumbColorOff(v: Int?): Switch {
        thumbColorOffValue = v
        return this
    }

    fun thumbColorOff(): Int? = thumbColorOffValue

    fun thumbInset(v: Float?): Switch {
        thumbInsetValue = v
        return this
    }

    fun thumbInset(): Float? = thumbInsetValue

    fun trackCornerRadius(v: Float?): Switch {
        trackCornerRadiusValue = v
        return this
    }

    fun trackCornerRadius(): Float? = trackCornerRadiusValue

    fun thumbCornerRadius(v: Float?): Switch {
        thumbCornerRadiusValue = v
        return this
    }

    fun thumbCornerRadius(): Float? = thumbCornerRadiusValue

    fun hoverOverlayColor(v: Int?): Switch {
        hoverOverlayColorValue = v
        return this
    }

    fun hoverOverlayColor(): Int? = hoverOverlayColorValue

    fun hoverOverlayOpacity(v: Float?): Switch {
        hoverOverlayOpacityValue = v
        return this
    }

    fun hoverOverlayOpacity(): Float? = hoverOverlayOpacityValue

    fun pressedOverlayColor(v: Int?): Switch {
        pressedOverlayColorValue = v
        return this
    }

    fun pressedOverlayColor(): Int? = pressedOverlayColorValue

    fun pressedOverlayOpacity(v: Float?): Switch {
        pressedOverlayOpacityValue = v
        return this
    }

    fun pressedOverlayOpacity(): Float? = pressedOverlayOpacityValue

    fun focusOverlayColor(v: Int?): Switch {
        focusOverlayColorValue = v
        return this
    }

    fun focusOverlayColor(): Int? = focusOverlayColorValue

    fun focusOverlayOpacity(v: Float?): Switch {
        focusOverlayOpacityValue = v
        return this
    }

    fun focusOverlayOpacity(): Float? = focusOverlayOpacityValue

    private fun toBooleanValue(v: Any?): Boolean {
        // 加固：若误把 onChange 的事件 Map（{value=Boolean,...}）写回 enabled state，取 value 字段兜底
        val mapValue = (v as? Map<*, *>)?.get("value")
        return when {
            v is Boolean -> v
            mapValue is Boolean -> mapValue
            else -> throw IllegalStateException("Switch 绑定的 enabled state 必须是布尔值: $v")
        }
    }

    companion object {
        /** 创建一个开关控件。 */
        @JvmStatic
        fun create(): Switch = Switch()
    }
}
